import {
  Box,
  DialogContent,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Alert,
  MenuItem,
} from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { useAllotments } from '../../../../../hooks/useAllotments';
import { useLots, useLot } from '../../../../../hooks/useLots';

/**
 * Tab para asignar un lote al proyecto (primero loteo, luego lote)
 * Muestra el contrato y sus suscriptores asociados al lote seleccionado.
 */
const LotTab = ({ localProject, onLotChange }) => {
  const { data: allotments = [] } = useAllotments();
  const [selectedAllotment, setSelectedAllotment] = useState('');
  const [selectedLot, setSelectedLot] = useState(null);

  const { data: lotDetail } = useLot(localProject?.uuid_lot, { enabled: !!localProject?.uuid_lot });
  const { data: lots = [], isLoading } = useLots({
    uuid_allotment: selectedAllotment,
    only_associated: true,
  });

  useEffect(() => {
    if (!localProject?.uuid_lot) {
      setSelectedAllotment('');
      setSelectedLot(null);
      return;
    }
    if (lotDetail?.uuid) {
      setSelectedAllotment(lotDetail.uuid_allotment || '');
      setSelectedLot(lotDetail);
    }
  }, [localProject?.uuid_lot, lotDetail]);

  const availableLots = useMemo(() => {
    const filteredLots = lots.filter((lot) => {
      if (localProject?.uuid_lot && lot.uuid === localProject.uuid_lot) {
        return true;
      }
      const hasContract = Boolean(lot?.uuid_contract || lot?.contract?.uuid);
      const hasConstructoraColonial = lot?.person_name === 'Constructora Colonial';
      const hasAssociation = hasContract || hasConstructoraColonial;
      if (!hasAssociation) return false;
      if (lot?.project) return false;
      if (lot?.project_is_in_progress || lot?.project_is_completed) return false;
      return true;
    });

    const lotLabel = (lot) =>
      `${lot.block_number || ''}${lot.block_number ? '-' : ''}${lot.lot_number || ''}`.trim();

    const collator = new Intl.Collator('es-AR', { numeric: true, sensitivity: 'base' });

    return [...filteredLots].sort((a, b) => collator.compare(lotLabel(a), lotLabel(b)));
  }, [lots, localProject?.uuid_lot]);

  const handleAllotmentChange = (event) => {
    const value = event.target.value;
    setSelectedAllotment(value);
    setSelectedLot(null);
    onLotChange?.(null);
  };

  const handleLotChange = (event, newValue) => {
    setSelectedLot(newValue);
    onLotChange?.(newValue?.uuid || null);
  };

  const selectedContract = selectedLot?.contract || null;
  const selectedSubscribers = selectedContract?.subscribers || [];

  return (
    <Box sx={{ width: '100%' }}>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Seleccionar loteo
            </Typography>

            <TextField
              select
              fullWidth
              label="Loteo"
              value={selectedAllotment}
              onChange={handleAllotmentChange}
            >
              <MenuItem value="">Seleccionar loteo</MenuItem>
              {allotments.map((allotment) => (
                <MenuItem key={allotment.uuid} value={allotment.uuid}>
                  {allotment.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Seleccionar lote
            </Typography>

            <Autocomplete
              value={selectedLot}
              onChange={handleLotChange}
              options={availableLots}
              getOptionLabel={(option) =>
                `${option.block_number || ''}${option.block_number ? '-' : ''}${option.lot_number}`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar lote"
                  variant="outlined"
                  fullWidth
                />
              )}
              loading={isLoading}
              loadingText="Cargando lotes..."
              noOptionsText="No se encontraron lotes"
              isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
              disabled={!selectedAllotment}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    opacity: 0.8,
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 3,
                    opacity: 0.6,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 3,
                  },
                },
              }}
            />
          </Box>

          {selectedContract && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Contrato del lote
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  # {selectedContract.contract_number}
                </Typography>
              </Alert>

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Suscriptores del Contrato:
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSubscribers.map((subscriber) => (
                  <Chip
                    key={subscriber.uuid}
                    label={`${subscriber.first_name} ${subscriber.last_name}`}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontWeight: 'bold',
                      '& .MuiChip-label': {
                        px: 2
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {!selectedLot && (
            <Alert severity="warning">
              <Typography>
                Debes seleccionar un lote para asociarlo al proyecto.
              </Typography>
            </Alert>
          )}

          {selectedContract && selectedSubscribers.length === 0 && (
            <Alert severity="error">
              <Typography>
                El contrato seleccionado no tiene suscriptores asociados.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Box>
  );
};

export default LotTab;
